import cx from "classnames";
import { throttle } from "lodash";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import CentralizedModalContainer from "../../Utils/CentralizedModalContainer/CentralizedModalContainer";
import { GetWSMgr } from "@/Utils/StateLib";
import { isNullish, useEventListener } from "@theorgchartdev/storybook";
import { useTopic } from "@/hooks/useTopic";
import { handleMouseMove } from "@coreapp/utils/handleMouseMove";
import { checkhysteresis } from "@coreapp/utils/checkhysteresis";
import { enumBoxShapes } from "@coreapp/constants";
import {
  getContainerHeight,
  getContainerOffset,
  getContainerWidth
} from "@/Utils/getContainerSize";

export const ChartBoxDragger = () => {
  const originPosition = useRef<null | { x: number; y: number }>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerSizeRef = useRef({ width: 0, height: 0, left: 0, top: 0 });
  // Origin of the position:fixed overlay that the absolute ghost is positioned within
  // (distinct from the container's viewport offset used for the bounds guard below).
  const [overlayOffset, setOverlayOffset] = useState({ x: 0, y: 0 });
  const [showDragger, setShowDragger] = useState(false);
  const [isValidDrop, setIsValidDrop] = useState({ canvas: false, dropArea: false });
  const [draggerPosition, setDraggerPosition] = useState({ x: 0, y: 0 });
  const [draggerDimensions, setDraggerDimensions] = useState<{ width: number; height: number }>({
    width: 125,
    height: 50
  });
  const [borderClass, setBorderClass] = useState<"oc-rounded" | undefined>("oc-rounded");

  useTopic("dragBoxValidCanvasTarget", ({ validHit }: { validHit: boolean }) => {
    setIsValidDrop(prev => ({
      ...prev,
      canvas: validHit
    }));
  });

  useTopic("dragBoxValidDropAreaTarget", ({ validHit }: { validHit: boolean }) => {
    setIsValidDrop(prev => ({
      ...prev,
      dropArea: validHit
    }));
  });

  useTopic(
    "dragStart",
    ({
      event,
      dimensions,
      boxShape
    }: {
      event: MouseEvent;
      dimensions?: { width: number; height: number } | null;
      boxShape?: (typeof enumBoxShapes)[keyof typeof enumBoxShapes] | null;
    }) => {
      setIsValidDrop({
        canvas: false,
        dropArea: false
      });
      setDraggerPosition({ x: event.clientX, y: event.clientY });
      originPosition.current = { x: event.clientX, y: event.clientY };
      const containerViewportOffset = getContainerOffset();
      containerSizeRef.current = {
        width: getContainerWidth(),
        height: getContainerHeight(),
        left: containerViewportOffset.left,
        top: containerViewportOffset.top
      };

      if (dimensions?.width && dimensions?.height) {
        setDraggerDimensions({
          width: dimensions.width,
          height: dimensions.height
        });
      }

      if (boxShape != null)
        setBorderClass(boxShape !== enumBoxShapes.roundRectangle ? undefined : "oc-rounded");
    }
  );

  useEventListener(
    "mouseup",
    () => {
      setShowDragger(false);
      originPosition.current = null;
    },
    { enabled: showDragger }
  );

  // The ghost is `position: absolute` inside the `position: fixed` overlay, so its
  // top/left resolve against the overlay's origin.
  useLayoutEffect(() => {
    if (!showDragger) return;
    const rect = overlayRef.current?.getBoundingClientRect();
    if (rect) setOverlayOffset({ x: rect.left, y: rect.top });
  }, [showDragger]);

  const canvasMouseMove = useCallback(
    throttle((e: MouseEvent) => {
      const wsMgr = GetWSMgr();
      const canvas = wsMgr?.fCanvas;
      handleMouseMove(canvas)(e);
    }, 100),
    []
  );

  useEventListener(
    "mousemove",
    (e: any) => {
      // Bitwise operation
      // Reference: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      const isPrimaryMouseClicked = (e.buttons & 1) === 1;
      if (!isPrimaryMouseClicked) {
        setShowDragger(false);
        originPosition.current = null;
        return;
      }

      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Pointer coords are viewport-relative; convert into container space so the
      // bounds guard is correct when .appContainer is offset from the viewport (MFE).
      const relX = mouseX - containerSizeRef.current.left;
      const relY = mouseY - containerSizeRef.current.top;

      const isXOutOfBounds = relX < 0 || relX > containerSizeRef.current.width;
      const isYOutOfBounds = relY < 0 || relY > containerSizeRef.current.height;
      if (isXOutOfBounds || isYOutOfBounds) {
        return;
      }

      if (!showDragger) {
        const originX = originPosition.current?.x ?? 0;
        const originY = originPosition.current?.y ?? 0;
        if (!checkhysteresis(mouseX, mouseY, originX, originY)) {
          setShowDragger(true);
        }
      }

      setDraggerPosition({
        x: mouseX,
        y: mouseY
      });

      canvasMouseMove(e);
    },
    { enabled: !isNullish(originPosition.current) }
  );

  if (!showDragger) {
    return null;
  }

  const width = draggerDimensions.width;
  const height = draggerDimensions.height;

  const borderColorClass =
    isValidDrop?.canvas || isValidDrop?.dropArea ? "oc-border-success" : "oc-border-warning";

  return (
    <CentralizedModalContainer show={showDragger}>
      <div ref={overlayRef} className="oc-card-wrapper-dragger-overlay">
        <div
          data-testid="chart-box-dragger"
          className={cx("oc-border", "oc-card-wrapper-dragger", borderClass, borderColorClass)}
          style={{
            position: "absolute",
            top: draggerPosition.y - height / 2 - overlayOffset.y,
            left: draggerPosition.x - width / 2 - overlayOffset.x,
            width,
            height,
            backgroundColor: "rgba(202, 229, 255, 0.5)"
          }}
        />
      </div>
    </CentralizedModalContainer>
  );
};
